import React, { FC, useState } from 'react';

import { Spin } from 'antd';

import { ColumnsType } from 'antd/es/table';

import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import { NSKeys } from '@app/i18n';
import { ArtifactItemSchema, RetrieveOrderSchema, WarehouseFlowSchema } from '@app/models/orders';
import { ORDERS_TABLE_COLUMNS, ORDERS_TABLE_SORTING } from '@app/services/appSettings';
import { useTableParams } from '@app/services/hooks/useTableParams';
import { useWindowSize } from '@app/services/hooks/useWindowSize';
import { TableDnDResizable } from '@components/TableDnDResizable';
import { TableEditMenu } from '@components/TableEditMenu';
import {
  getDefaultSortOrderByParsedValues,
  getFilters,
  getSorterFlag,
  getSortValues,
  getTableItems,
  WarehouseErrorMarker,
} from '@components/orders';

import { useGetOrdersByProcessQuery, useGetProcessQuery } from '../../../../../../hooks';

import { useProcessDataParams } from '../../../../../../providers';
import { Filters } from '../../../../components/Filters';

import { RowsActions } from './components/RowsActions';
import { Container } from './styles';

export const TableContainer: FC = () => {
  const params = useParams<{ processId: string }>();
  const navigate = useNavigate();
  const commonT = useTranslation(NSKeys.common);
  const ordersT = useTranslation(NSKeys.orders);
  const [selectedRows, setSelectedRows] = React.useState<{ uid: string; version: string }[]>([]);
  const [query, setQuery] = React.useState<string>('');
  const [baseColumns, setBaseColumns] = useState<ColumnsType<RetrieveOrderSchema> | undefined>();
  const { innerHeight } = useWindowSize();
  const [localStorageColumns, setLocalStorageColumns] = useLocalStorage<ColumnsType<RetrieveOrderSchema> | undefined>(
    `${params.processId}${ORDERS_TABLE_COLUMNS}`,
    undefined
  );
  const [localStorageSorting, setLocalStorageSorting] = useLocalStorage<string[]>(
    `${params.processId || ''}${ORDERS_TABLE_SORTING}`,
    []
  );
  const { current, pageSize, setPagination, resetParams } = useTableParams();
  const { data: processData } = useGetProcessQuery(params.processId);
  const { data: tableData, isLoading } = useGetOrdersByProcessQuery({
    offset: (current - 1) * pageSize,
    limit: pageSize,
    process_uid: params.processId,
    sort: localStorageSorting,
    query,
  });
  const { setQueryParams, resetQueryParams } = useProcessDataParams();

  React.useEffect(() => {
    setQueryParams({ sort: localStorageSorting, query });
  }, [localStorageSorting, query]);

  React.useEffect(() => {
    return () => {
      resetQueryParams();
    };
  }, []);

  React.useEffect(() => {
    if (processData) {
      const data = getTableItems({ processData, splitFields: true, t: ordersT.t });
      if (!data) {
        return;
      }

      const serializableColumns = data.map(({ render, ...rest }) => rest);
      setBaseColumns(data);

      if (!localStorageColumns) {
        setLocalStorageColumns(serializableColumns);
        return;
      }

      const baseColumnKeys = new Set(serializableColumns.map((column) => column.key));
      const normalizedColumns = localStorageColumns.filter((column) => baseColumnKeys.has(column.key));

      if (normalizedColumns.length !== localStorageColumns.length) {
        setLocalStorageColumns(normalizedColumns);
      }
    }
  }, [localStorageColumns, ordersT.t, processData, setLocalStorageColumns]);

  const filters = React.useMemo(() => {
    if (processData && params.processId) {
      const columns = getTableItems({ processData, splitFields: false, t: ordersT.t });
      return columns?.length ? getFilters(columns, commonT.t, params.processId) : [];
    }
  }, [processData, params.processId]);

  const warehouseFlowArtifactUid = processData?.order_structure?.items?.find(
    (item): item is ArtifactItemSchema => item.type === 'artifact' && item.data.type === 'warehouse_flow'
  )?.data?.uid;

  const warehouseErrorColumn: ColumnsType<RetrieveOrderSchema>[number] | undefined = warehouseFlowArtifactUid
    ? {
        key: 'warehouseError',
        width: 40,
        fixed: 'left',
        align: 'center',
        title: '',
        render: (_, record) => {
          const flow = (record.artifacts as Record<string, any> | null | undefined)?.[warehouseFlowArtifactUid] as
            | WarehouseFlowSchema
            | null
            | undefined;
          return <WarehouseErrorMarker error={flow?.last_error_message} fontSize={16} />;
        },
      }
    : undefined;

  const handleTableHeaderChange = (checkedValues: any[]) => {
    baseColumns &&
      setLocalStorageColumns(
        baseColumns
          .map((col) => ({
            ...col,
            width: localStorageColumns?.find((localEl) => localEl.key === col.key)?.width || col.width,
          }))
          .filter((item) => checkedValues.includes(item.key as string))
      );
  };

  if (!baseColumns || !localStorageColumns) {
    return <Spin />;
  }

  return (
    <Container style={{ position: 'relative' }}>
      {!!selectedRows.length && params.processId && (
        <RowsActions selectedRows={selectedRows} onDone={() => setSelectedRows([])} process_uid={params.processId} />
      )}
      <Filters onChange={setQuery} fields={filters || []} uidPrefix={params?.processId} />
      <TableDnDResizable<RetrieveOrderSchema>
        localStorageName={`${params.processId}${ORDERS_TABLE_COLUMNS}`}
        staticColumns={warehouseErrorColumn ? [warehouseErrorColumn] : undefined}
        columns={[
          ...localStorageColumns.map((el, index) => ({
            ...el,
            sorter: getSorterFlag(el, index),
            defaultSortOrder: getDefaultSortOrderByParsedValues(el.key as string, localStorageSorting),
            render: baseColumns?.find((baseCol) => baseCol.key === el.key)?.render,
          })),
        ]}
        actionColumn={[
          {
            title: (
              <TableEditMenu
                defaultValue={localStorageColumns?.map((el) => el.key as string)}
                handleOnChange={handleTableHeaderChange}
                options={baseColumns?.map((el) => ({
                  value: el.key as string,
                  label: el.title as string,
                }))}
              />
            ),
            key: 'action',
            fixed: 'right',
            align: 'center',
            width: 40,
          },
        ]}
        onChange={(pagination, filters, sorter) => {
          setLocalStorageSorting(getSortValues(sorter as any));
          setSelectedRows([]);
        }}
        showSorterTooltip
        pagination={{
          current: current,
          pageSize: pageSize,
          total: tableData?.total,
          showQuickJumper: true,
          showSizeChanger: true,
          position: ['bottomCenter'],
          showTotal: (total, range) => `${range[0]}-${range[1]} ${commonT.t('paginationOf')} ${total}`,
          onChange: (newPage, newSize) => {
            setPagination(newPage, newSize);
          },
        }}
        key="uid"
        rowKey="uid"
        onColumnsChange={setLocalStorageColumns}
        rowClassName="cursorPointer"
        className="tableClassname"
        loading={isLoading}
        dataSource={tableData?.items}
        style={{ marginTop: 24 }}
        scroll={{ y: innerHeight - 380 }}
        onRow={(data) => {
          return {
            onClick: () => data?.$permissions?.view && navigate(`${data.uid}`),
          };
        }}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedRows.map((el) => el.uid),
          renderCell: (value, record, index, originNode) => (
            <div onClick={(e) => e.stopPropagation()}>{originNode}</div>
          ),
          onChange: (selectedRowKeys, data) => {
            setSelectedRows(
              data?.map((el) => {
                return { uid: el.uid, version: el.version };
              }) as any[]
            );
          },
        }}
      />
    </Container>
  );
};
